import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
      if (res.data?.dev_mode && res.data?.dev_reset_url) {
        setDevResetUrl(res.data.dev_reset_url);
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="forgot-password-page">
      <div className="noise-overlay" />
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />
      <div className="floating-orb floating-orb-gold" style={{ width: 60, height: 60, top: '20%', right: '20%', animationDuration: '8s', zIndex: 1 }} />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="inline-flex items-center gap-2 text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase mb-10"
            style={{ transition: 'color 0.2s ease' }}
          >
            <ArrowLeft size={14} /> Back to sign in
          </Link>

          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Account Recovery</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight leading-tight mystical-heading shimmer-text">
            Reset your password
          </h1>
          <p className="text-text-muted font-sans text-base mt-4 leading-relaxed">
            Enter your email and we'll send you a secure link to choose a new password.
          </p>
          <div className="mystical-divider mt-6 mb-8" />

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-text-muted text-xs tracking-wider uppercase font-sans">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  placeholder="you@example.com"
                  required
                  className="h-12 bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
                />
              </div>
              <Button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={submitting || !email.trim()}
                className="w-full h-12 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider uppercase btn-hover"
              >
                {submitting ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
          ) : (
            <div className="mystical-card p-8 text-center" data-testid="forgot-success">
              <div className="relative w-16 h-16 mx-auto mb-5">
                <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
                <div className="absolute inset-2 rounded-full bg-gold/10 animate-breathe flex items-center justify-center" style={{ animationDelay: '0.5s' }}>
                  <Mail size={20} className="text-gold" />
                </div>
              </div>
              <h3 className="font-serif text-2xl text-dark mb-3">Check your inbox</h3>
              <p className="font-sans text-sm text-text-muted leading-relaxed mb-2">
                If an account exists for <span className="text-dark">{email}</span>, a reset link has been sent.
              </p>
              <p className="font-sans text-xs text-text-muted/70">
                The link is valid for 60 minutes.
              </p>

              {devResetUrl && (
                <div className="mt-6 p-4 rounded-xl bg-gold/10 border border-gold/30 text-left" data-testid="dev-reset-url-box">
                  <p className="text-gold font-sans text-[10px] tracking-[0.25em] uppercase mb-2">Dev Mode — Email not configured</p>
                  <p className="font-sans text-xs text-text-muted mb-2 leading-relaxed">
                    Use this link to reset (would normally be emailed):
                  </p>
                  <a
                    href={devResetUrl}
                    data-testid="dev-reset-link"
                    className="font-mono text-xs text-dark break-all underline decoration-gold/40 hover:decoration-gold"
                  >
                    {devResetUrl}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
